import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "../../lib/api";
import { formatSgd, formatNum } from "../../lib/utils";
import { PageShell, Stat } from "./PageShell";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input, Label } from "../../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Wallet, TrendingDown, TrendingUp, Percent, Download, SlidersHorizontal, Plus, Trash2, X, Target, BarChart3 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer } from "recharts";
import { toast } from "sonner";

const COST_LABEL = { hosting: "Platform / hosting", storage: "Storage", ai: "AI usage", professional_services: "Professional services", other: "Other operating" };
const REV_LABEL = { subscription: "Subscriptions", addons: "Add-ons", professional_services: "Professional services", discount: "Discounts" };

const money = (n) => formatSgd(n || 0);
const profitTone = (n) => (n > 0 ? "text-accent" : n < 0 ? "text-red-400" : "");
const MarginBadge = ({ v }) => <Badge variant={v >= 50 ? "success" : v >= 0 ? "outline" : "destructive"} className="text-[10px] font-mono">{v}%</Badge>;

function Strip({ title, items, labelMap, signFlip }) {
  const entries = Object.entries(items).filter(([k]) => k !== "total");
  return (
    <div className="card-flat p-4">
      <div className="label-eyebrow mb-3">{title}</div>
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {entries.map(([k, v]) => (
          <div key={k} data-testid={`strip-${title}-${k}`}>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{labelMap[k] || k}</div>
            <div className="font-mono text-sm font-semibold">{money(signFlip && k === "discount" ? -v : v)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PlatformAccounting() {
  const [period, setPeriod] = useState(null);
  const [basis, setBasis] = useState("estimate");
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [tenantId, setTenantId] = useState(null);
  const [sort, setSort] = useState({ key: "profit", dir: -1 });

  const load = (p, b = basis) => api.get("/accounting/summary", { params: { period: p || undefined, basis: b } })
    .then((r) => { setData(r.data); if (!period) setPeriod(r.data.period); })
    .catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(period, basis); /* eslint-disable-next-line */ }, [period, basis]);
  useEffect(() => {
    api.get("/accounting/trends", { params: { year: (period || "").slice(0, 4) || undefined, basis } })
      .then((r) => setTrends(r.data)).catch(() => setTrends(null));
    /* eslint-disable-next-line */
  }, [period, basis]);

  const tenants = useMemo(() => {
    if (!data) return [];
    const rows = [...data.tenants];
    rows.sort((a, b) => {
      const g = (r) => (sort.key === "name" ? r.tenant.name : r[sort.key]);
      const av = g(a), bv = g(b);
      if (typeof av === "string") return sort.dir * av.localeCompare(bv);
      return sort.dir * ((av || 0) - (bv || 0));
    });
    return rows;
  }, [data, sort]);

  const setSortKey = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : -1 }));
  const exportCsv = async () => {
    try {
      const r = await api.get(`/accounting/export.csv?period=${period}&basis=${basis}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `platform_accounting_${period}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!data) return <PageShell title="Accounting" testid="platform-accounting-page"><div className="text-muted-foreground font-mono text-sm">Loading…</div></PageShell>;
  const s = data.summary;

  return (
    <PageShell eyebrow="Platform Operations" title="Accounting" testid="platform-accounting-page"
      subtitle="Revenue, cost and profitability across all tenants — management accounts in SGD."
      actions={
        <div className="flex items-center gap-2">
          <Select value={basis} onValueChange={setBasis}>
            <SelectTrigger className="w-40" data-testid="acc-basis-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="estimate">Estimated</SelectItem>
              <SelectItem value="actual">Actual (invoiced)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={period || ""} onValueChange={setPeriod}>
            <SelectTrigger className="w-48" data-testid="acc-period-select"><SelectValue placeholder="Period" /></SelectTrigger>
            <SelectContent>{(data.period_options || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => setBudgetOpen(true)} data-testid="acc-budget-btn"><Target className="h-3.5 w-3.5" /> Budget</Button>
          <Button variant="outline" size="sm" onClick={() => setCfgOpen(true)} data-testid="acc-cost-model-btn"><SlidersHorizontal className="h-3.5 w-3.5" /> Cost model</Button>
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="acc-export-btn"><Download className="h-3.5 w-3.5" /> CSV</Button>
        </div>
      }>
      {/* P&L summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Wallet} label="Total revenue" value={money(s.revenue.total)} sub={`${data.period_label} · ${data.active_tenants} paying tenants`} />
        <Stat icon={TrendingDown} label="Total cost" value={money(s.cost.total)} tone="amber" />
        <Stat icon={TrendingUp} label="Gross profit" value={money(s.profit)} tone={s.profit >= 0 ? "accent" : "red"} />
        <Stat icon={Percent} label="Gross margin" value={`${s.margin_pct}%`} tone={s.margin_pct >= 0 ? "accent" : "red"} sub={basis === "actual" ? "from issued invoices" : "catalogue estimate"} />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Strip title="Revenue" items={s.revenue} labelMap={REV_LABEL} signFlip />
        <Strip title="Cost" items={s.cost} labelMap={COST_LABEL} />
      </div>

      <TrendsChart trends={trends} />
      {data.budget && <BudgetVsActual budget={data.budget} onEdit={() => setBudgetOpen(true)} />}

      {/* By plan */}
      <div className="card-flat p-4">
        <div className="label-eyebrow mb-2">Profitability by plan</div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Plan</TableHead><TableHead className="text-right">Tenants</TableHead>
            <TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">Cost</TableHead>
            <TableHead className="text-right">Profit</TableHead><TableHead className="text-right">Margin</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.by_plan.map((p) => (
              <TableRow key={p.plan_code} data-testid={`acc-plan-${p.plan_code}`}>
                <TableCell className="font-medium text-sm">{p.plan_name}</TableCell>
                <TableCell className="text-right font-mono text-sm">{p.tenants}</TableCell>
                <TableCell className="text-right font-mono text-sm">{money(p.revenue)}</TableCell>
                <TableCell className="text-right font-mono text-sm">{money(p.cost)}</TableCell>
                <TableCell className={`text-right font-mono text-sm ${profitTone(p.profit)}`}>{money(p.profit)}</TableCell>
                <TableCell className="text-right"><MarginBadge v={p.margin_pct} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* By add-on */}
      {data.by_addon.length > 0 && (
        <div className="card-flat p-4">
          <div className="label-eyebrow mb-2">Add-on modules — revenue vs allocated cost</div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Add-on</TableHead><TableHead className="text-right">Tenants</TableHead>
              <TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">Alloc. cost</TableHead>
              <TableHead className="text-right">Profit</TableHead><TableHead className="text-right">Margin</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.by_addon.map((a) => (
                <TableRow key={a.key} data-testid={`acc-addon-${a.key}`}>
                  <TableCell className="font-medium text-sm">{a.name}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{a.tenants}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{money(a.revenue)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{money(a.cost)}</TableCell>
                  <TableCell className={`text-right font-mono text-sm ${profitTone(a.profit)}`}>{money(a.profit)}</TableCell>
                  <TableCell className="text-right"><MarginBadge v={a.margin_pct} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Per tenant */}
      <div className="card-flat p-4">
        <div className="label-eyebrow mb-2">Per-tenant accounting · click a row for the full statement</div>
        <Table>
          <TableHeader><TableRow>
            {[["name", "Tenant"], ["plan", "Plan"]].map(([k, l]) => <TableHead key={k} className="cursor-pointer" onClick={() => setSortKey(k === "plan" ? "plan_name" : "name")}>{l}</TableHead>)}
            {[["revenue", "Revenue"], ["cost", "Cost"], ["profit", "Profit"], ["margin_pct", "Margin"]].map(([k, l]) => (
              <TableHead key={k} className="text-right cursor-pointer" onClick={() => setSortKey(k)} data-testid={`acc-sort-${k}`}>{l}{sort.key === k ? (sort.dir < 0 ? " ↓" : " ↑") : ""}</TableHead>
            ))}
          </TableRow></TableHeader>
          <TableBody>
            {tenants.map((r) => (
              <TableRow key={r.tenant.id} className="cursor-pointer" onClick={() => setTenantId(r.tenant.id)} data-testid={`acc-tenant-row-${r.tenant.id}`}>
                <TableCell className="font-medium text-sm">{r.tenant.name}{r.has_override && <Badge variant="outline" className="ml-2 text-[8px]">custom</Badge>}</TableCell>
                <TableCell className="text-xs">{r.plan_name}</TableCell>
                <TableCell className="text-right font-mono text-sm">{money(r.revenue)}</TableCell>
                <TableCell className="text-right font-mono text-sm">{money(r.cost)}</TableCell>
                <TableCell className={`text-right font-mono text-sm ${profitTone(r.profit)}`}>{money(r.profit)}</TableCell>
                <TableCell className="text-right"><MarginBadge v={r.margin_pct} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="text-[10px] text-muted-foreground">Figures are management-accounts estimates in SGD. {basis === "actual" ? "Revenue is taken from issued invoices; AI cost is metered at-cost; hosting stays rate-based (no cost feed)." : "Revenue and costs use the catalogue and Cost-model rates; usage quantities (AI tokens, storage) are measured automatically."}</p>

      <CostModelDialog open={cfgOpen} onClose={() => setCfgOpen(false)} cfg={data.cost_model} onSaved={() => { setCfgOpen(false); load(period); }} />
      <BudgetDialog open={budgetOpen} onClose={() => setBudgetOpen(false)} cfg={data.cost_model} onSaved={() => { setBudgetOpen(false); load(period); }} />
      {tenantId && <TenantStatementDialog tid={tenantId} period={period} basis={basis} onClose={() => setTenantId(null)} onChanged={() => load(period)} />}
    </PageShell>
  );
}

function CostModelDialog({ open, onClose, cfg, onSaved }) {
  const [f, setF] = useState(cfg);
  useEffect(() => { setF(cfg); }, [cfg, open]);
  const lines = f.other_cost_lines || [];
  const save = async () => {
    try {
      await api.put("/accounting/config", {
        hosting_sgd_per_month: Number(f.hosting_sgd_per_month) || 0,
        storage_sgd_per_gb_month: Number(f.storage_sgd_per_gb_month) || 0,
        ai_cost_sgd_per_1k_tokens: Number(f.ai_cost_sgd_per_1k_tokens) || 0,
        avg_tokens_per_call: Number(f.avg_tokens_per_call) || 0,
        other_cost_lines: lines.map((l) => ({ label: l.label, kind: l.kind, value: Number(l.value) || 0 })),
      });
      toast.success("Cost model saved"); onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const setLine = (i, patch) => setF((p) => ({ ...p, other_cost_lines: lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="acc-cost-model-dialog">
        <DialogHeader><DialogTitle>Cost model</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-[11px] text-muted-foreground">Enter the rate assumptions. Usage (AI tokens, storage GB, active tenants) is measured automatically — you maintain only the rates.</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Hosting — S$ / month (platform-wide)</Label><Input type="number" value={f.hosting_sgd_per_month ?? ""} onChange={(e) => setF((p) => ({ ...p, hosting_sgd_per_month: e.target.value }))} data-testid="cfg-hosting" /></div>
            <div><Label className="text-xs">Storage — S$ / GB-month</Label><Input type="number" step="0.001" value={f.storage_sgd_per_gb_month ?? ""} onChange={(e) => setF((p) => ({ ...p, storage_sgd_per_gb_month: e.target.value }))} data-testid="cfg-storage" /></div>
            <div><Label className="text-xs">AI cost — S$ / 1k tokens</Label><Input type="number" step="0.001" value={f.ai_cost_sgd_per_1k_tokens ?? ""} onChange={(e) => setF((p) => ({ ...p, ai_cost_sgd_per_1k_tokens: e.target.value }))} data-testid="cfg-ai-rate" /></div>
            <div><Label className="text-xs">Avg tokens / AI call</Label><Input type="number" value={f.avg_tokens_per_call ?? ""} onChange={(e) => setF((p) => ({ ...p, avg_tokens_per_call: e.target.value }))} data-testid="cfg-avg-tokens" /></div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1"><Label className="text-xs">Other operating cost lines</Label>
              <Button variant="ghost" size="sm" onClick={() => setF((p) => ({ ...p, other_cost_lines: [...lines, { label: "", kind: "fixed_monthly", value: 0 }] }))} data-testid="cfg-add-other"><Plus className="h-3.5 w-3.5" /> Add line</Button></div>
            {lines.map((l, i) => (
              <div key={i} className="flex items-center gap-2 mb-1.5" data-testid={`cfg-other-${i}`}>
                <Input placeholder="Label (e.g. Payment processing)" value={l.label} onChange={(e) => setLine(i, { label: e.target.value })} className="h-8 text-xs" />
                <Select value={l.kind} onValueChange={(v) => setLine(i, { kind: v })}>
                  <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="fixed_monthly">Fixed / month</SelectItem><SelectItem value="pct_revenue">% of revenue</SelectItem></SelectContent>
                </Select>
                <Input type="number" value={l.value} onChange={(e) => setLine(i, { value: e.target.value })} className="h-8 w-24 text-xs" />
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setF((p) => ({ ...p, other_cost_lines: lines.filter((_, j) => j !== i) }))}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={save} data-testid="cfg-save">Save cost model</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TrendsChart({ trends }) {
  const pts = (trends && trends.points) || [];
  return (
    <div className="card-flat p-4" data-testid="acc-trends-card">
      <div className="label-eyebrow mb-2 flex items-center gap-2"><BarChart3 className="h-3.5 w-3.5" /> Revenue · cost · profit — monthly trend{trends ? ` · ${trends.year}` : ""}</div>
      {pts.length === 0 ? (
        <div className="text-muted-foreground text-xs font-mono py-10 text-center" data-testid="acc-trends-empty">No monthly data yet.</div>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={pts} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} stroke="rgba(148,163,184,0.3)" />
            <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} stroke="rgba(148,163,184,0.3)" width={56} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
            <RTooltip formatter={(v, n) => [money(v), n]} contentStyle={{ background: "#0f1715", border: "1px solid rgba(148,163,184,0.25)", fontSize: 12, borderRadius: 8 }} labelStyle={{ color: "#94a3b8" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#34d399" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="cost" name="Cost" stroke="#fbbf24" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="profit" name="Profit" stroke="#60a5fa" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function BudgetVsActual({ budget, onEdit }) {
  const row = (label, b, key) => (
    <TableRow key={key} data-testid={`budget-row-${key}`}>
      <TableCell className="text-sm">{label}</TableCell>
      <TableCell className="text-right font-mono text-sm">{money(b.target)}</TableCell>
      <TableCell className="text-right font-mono text-sm">{money(b.actual)}</TableCell>
      <TableCell className={`text-right font-mono text-sm ${b.variance >= 0 ? "text-accent" : "text-red-400"}`}>{b.variance >= 0 ? "+" : ""}{money(b.variance)}</TableCell>
      <TableCell className="text-right font-mono text-xs text-muted-foreground">{b.pct == null ? "—" : `${b.pct}%`}</TableCell>
    </TableRow>
  );
  if (!budget.has_budget) {
    return (
      <div className="card-flat p-4 flex items-center justify-between" data-testid="acc-budget-empty">
        <div><div className="label-eyebrow mb-1">Budget vs actual</div><div className="text-xs text-muted-foreground">No monthly targets set yet — set a budget to track spend against plan.</div></div>
        <Button variant="outline" size="sm" onClick={onEdit} data-testid="acc-budget-set"><Target className="h-3.5 w-3.5" /> Set budget</Button>
      </div>
    );
  }
  return (
    <div className="card-flat p-4" data-testid="acc-budget-card">
      <div className="label-eyebrow mb-2 flex items-center justify-between">
        <span className="flex items-center gap-2"><Target className="h-3.5 w-3.5" /> Budget vs actual · {budget.months} mo (target = monthly × {budget.months})</span>
        <Button variant="ghost" size="sm" onClick={onEdit} data-testid="acc-budget-edit">Edit</Button>
      </div>
      <Table>
        <TableHeader><TableRow>
          <TableHead>Line</TableHead><TableHead className="text-right">Target</TableHead>
          <TableHead className="text-right">Actual</TableHead><TableHead className="text-right">Variance</TableHead>
          <TableHead className="text-right">Used</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {row("Revenue", budget.revenue, "revenue")}
          {Object.entries(budget.cost_lines).map(([k, b]) => row(COST_LABEL[k] || k, b, k))}
          {row("Total cost", budget.cost_total, "cost_total")}
          {row("Profit", budget.profit, "profit")}
        </TableBody>
      </Table>
    </div>
  );
}

function BudgetDialog({ open, onClose, cfg, onSaved }) {
  const [b, setB] = useState({});
  useEffect(() => { setB((cfg && cfg.budgets) || {}); }, [cfg, open]);
  const fields = [["revenue", "Revenue"], ["hosting", "Hosting"], ["storage", "Storage"], ["ai", "AI usage"], ["professional_services", "Professional services"], ["other", "Other operating"], ["cost_total", "Total cost"], ["profit", "Target profit"]];
  const save = async () => {
    try {
      const budgets = {};
      fields.forEach(([k]) => { budgets[k] = Number(b[k]) || 0; });
      await api.put("/accounting/config", { budgets });
      toast.success("Budget saved"); onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="acc-budget-dialog">
        <DialogHeader><DialogTitle>Monthly budget targets (SGD)</DialogTitle></DialogHeader>
        <p className="text-[11px] text-muted-foreground">Enter monthly targets. For a multi-month period the target is multiplied by the number of months. Leave 0 to ignore a line.</p>
        <div className="grid grid-cols-2 gap-3">
          {fields.map(([k, l]) => (
            <div key={k}>
              <Label className="text-xs">{l} — S$/mo</Label>
              <Input type="number" value={b[k] ?? ""} onChange={(e) => setB((p) => ({ ...p, [k]: e.target.value }))} data-testid={`budget-input-${k}`} />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={save} data-testid="budget-save">Save budget</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LineTable({ title, lines, total, tone }) {
  return (
    <div>
      <div className="label-eyebrow mb-1">{title}</div>
      <Table>
        <TableBody>
          {lines.map((l, i) => (
            <TableRow key={i}>
              <TableCell className="text-xs py-1.5">{l.label}<span className="text-[10px] text-muted-foreground ml-1">{l.qty ? `${formatNum(l.qty)} ${l.unit || ""} @ ${l.rate}` : l.unit}</span></TableCell>
              <TableCell className={`text-right font-mono text-xs py-1.5 ${l.amount < 0 ? "text-red-400" : ""}`}>{money(l.amount)}</TableCell>
            </TableRow>
          ))}
          <TableRow><TableCell className="text-xs font-semibold py-1.5">Total {title.toLowerCase()}</TableCell><TableCell className={`text-right font-mono text-sm font-bold py-1.5 ${tone || ""}`}>{money(total)}</TableCell></TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function TenantStatementDialog({ tid, period, basis, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [ovr, setOvr] = useState({ contract_price_sgd: "", discount_type: "none", discount_value: "" });
  const [ps, setPs] = useState({ description: "", revenue_sgd: "", cost_sgd: "", date: "" });
  const load = () => api.get(`/accounting/tenant/${tid}`, { params: { period, basis } }).then((r) => {
    setD(r.data);
    const o = r.data.override || {};
    setOvr({ contract_price_sgd: o.contract_price_sgd ?? "", discount_type: o.discount_type || "none", discount_value: o.discount_value ?? "" });
  }).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tid, period, basis]);

  const saveOverride = async () => {
    try {
      await api.put(`/accounting/tenant/${tid}/override`, {
        contract_price_sgd: ovr.contract_price_sgd === "" ? null : Number(ovr.contract_price_sgd),
        discount_type: ovr.discount_type === "none" ? null : ovr.discount_type,
        discount_value: ovr.discount_value === "" ? null : Number(ovr.discount_value),
      });
      toast.success("Revenue adjustment saved"); load(); onChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const addPs = async () => {
    try {
      await api.post(`/accounting/tenant/${tid}/ps`, { description: ps.description, revenue_sgd: Number(ps.revenue_sgd) || 0, cost_sgd: Number(ps.cost_sgd) || 0, date: ps.date });
      toast.success("Professional-services line added"); setPs({ description: "", revenue_sgd: "", cost_sgd: "", date: "" }); load(); onChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delPs = async (pid) => { try { await api.delete(`/accounting/ps/${pid}`); load(); onChanged(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="acc-tenant-dialog">
        {!d ? <div className="text-muted-foreground text-sm p-4">Loading…</div> : (
          <>
            <DialogHeader><DialogTitle className="flex items-center gap-2">{d.tenant.name}
              <Badge variant="outline" className="text-[10px]">{d.plan_name}</Badge>
              <span className="text-xs text-muted-foreground font-normal">· {d.period_label}</span></DialogTitle></DialogHeader>
            <div className="grid grid-cols-3 gap-3 mb-2">
              <Stat label="Revenue" value={money(d.revenue.total)} />
              <Stat label="Cost" value={money(d.cost.total)} tone="amber" />
              <Stat label="Profit" value={money(d.profit)} tone={d.profit >= 0 ? "accent" : "red"} sub={`${d.margin_pct}% margin`} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <LineTable title="Revenue" lines={d.revenue.lines} total={d.revenue.total} />
              <LineTable title="Cost" lines={d.cost.lines} total={d.cost.total} tone="text-amber-400" />
            </div>

            <div className="card-flat p-3 mt-3">
              <div className="label-eyebrow mb-2">Revenue adjustments (contract / discount){d.basis === "actual" && <span className="text-[10px] text-amber-400 ml-2 normal-case">· applies to Estimated basis</span>}</div>
              <div className="flex flex-wrap items-end gap-2">
                <div><Label className="text-[11px]">Contract price S$/mo</Label><Input type="number" placeholder="catalogue" value={ovr.contract_price_sgd} onChange={(e) => setOvr((p) => ({ ...p, contract_price_sgd: e.target.value }))} className="h-8 w-32 text-xs" data-testid="ovr-contract" /></div>
                <div><Label className="text-[11px]">Discount</Label>
                  <Select value={ovr.discount_type} onValueChange={(v) => setOvr((p) => ({ ...p, discount_type: v }))}>
                    <SelectTrigger className="w-28 h-8 text-xs" data-testid="ovr-disc-type"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None</SelectItem><SelectItem value="pct">% percent</SelectItem><SelectItem value="fixed">S$ fixed</SelectItem></SelectContent>
                  </Select></div>
                {ovr.discount_type !== "none" && <div><Label className="text-[11px]">Value</Label><Input type="number" value={ovr.discount_value} onChange={(e) => setOvr((p) => ({ ...p, discount_value: e.target.value }))} className="h-8 w-24 text-xs" data-testid="ovr-disc-value" /></div>}
                <Button variant="primary" size="sm" onClick={saveOverride} data-testid="ovr-save">Save</Button>
              </div>
            </div>

            <div className="card-flat p-3 mt-3">
              <div className="label-eyebrow mb-2">Professional services (revenue + cost to deliver)</div>
              {(d.professional_services || []).map((p) => (
                <div key={p.id} className="flex items-center justify-between text-xs border-b border-border py-1" data-testid={`ps-row-${p.id}`}>
                  <span className="truncate">{p.date} · {p.description}</span>
                  <span className="flex items-center gap-3 font-mono shrink-0"><span className="text-accent">{money(p.revenue_sgd)}</span><span className="text-amber-400">({money(p.cost_sgd)})</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => delPs(p.id)}><X className="h-3 w-3" /></Button></span>
                </div>
              ))}
              <div className="flex flex-wrap items-end gap-2 mt-2">
                <div><Label className="text-[11px]">Description</Label><Input value={ps.description} onChange={(e) => setPs((p) => ({ ...p, description: e.target.value }))} className="h-8 w-48 text-xs" data-testid="ps-desc" /></div>
                <div><Label className="text-[11px]">Revenue S$</Label><Input type="number" value={ps.revenue_sgd} onChange={(e) => setPs((p) => ({ ...p, revenue_sgd: e.target.value }))} className="h-8 w-24 text-xs" data-testid="ps-rev" /></div>
                <div><Label className="text-[11px]">Cost S$</Label><Input type="number" value={ps.cost_sgd} onChange={(e) => setPs((p) => ({ ...p, cost_sgd: e.target.value }))} className="h-8 w-24 text-xs" data-testid="ps-cost" /></div>
                <div><Label className="text-[11px]">Date</Label><Input type="date" value={ps.date} onChange={(e) => setPs((p) => ({ ...p, date: e.target.value }))} className="h-8 w-36 text-xs" data-testid="ps-date" /></div>
                <Button variant="outline" size="sm" disabled={!ps.description || !ps.date} onClick={addPs} data-testid="ps-add"><Plus className="h-3.5 w-3.5" /> Add</Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
